import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { ComplaintReception, Witness } from "types";
import WitnessForm from "../model/WitnessForm";
import WitnessDetail from "../model/WitnessDetail";
import DeleteModal from "components/DeleteModal";
import { deleteWitness } from "services/complaintDetailService";
import { errorHandler } from "utils/helper";

type Props = {
  compliant: ComplaintReception;
  setRefetch: any;
  canUpdate: boolean;
};

const WitnessTab: React.FC<Props> = ({ compliant, setRefetch, canUpdate }) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedWitness, setSelectedWitness] = React.useState<Witness>();

  const handleOpenForm = () => {
    setIsOpenForm(true);
  };

  const handleCloseForm = (refetch = false) => {
    setIsOpenForm(false);
    if (refetch) setRefetch();
  };

  const handleOpenDetail = () => {
    setIsOpenDetail(true);
  };

  const handleCloseDetail = (refetch = false) => {
    setIsOpenDetail(false);
    if (refetch) {
      setRefetch();
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Witnesses</h3>
          <p className="text-sm text-gray-500 mt-1">
            Individuals who can provide testimony
          </p>
        </div>
        {canUpdate && (
          <button
            onClick={() => {
              setAction("Add");
              handleOpenForm();
            }}
            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-11a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z"
              />
            </svg>
            Add Witness
          </button>
        )}
      </div>

      {compliant?.witness_info && compliant?.witness_info.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-11a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z"
              />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">
            No Witnesses Added
          </h4>
          <p className="text-gray-500 mb-4">
            Add witnesses who can testify about the incident
          </p>
          {canUpdate && (
            <button
              onClick={() => {
                setAction("Add");
                handleOpenForm();
              }}
              className="px-4 py-2 text-green-600 hover:text-green-800 font-medium"
            >
              Add your first witness →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {compliant?.witness_info?.map((witness, index) => (
            <div
              key={index}
              className="group border border-gray-200 hover:border-green-300 rounded-xl p-4 transition-all hover:shadow-md bg-white"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {witness?.full_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {witness?.full_name}
                      </h4>
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Witness
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <svg
                          className="w-4 h-4 mr-2 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        {witness?.phone_number || "Not provided"}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <svg
                          className="w-4 h-4 mr-2 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        {witness?.email || "Not provided"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {[
                          witness?.region,
                          witness?.zone_or_city,
                          witness?.woreda_or_kebele,
                          witness?.sub_city,
                          witness?.kebele,
                          witness?.house_number,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                      {/* {witness?.statement && (
                        <div className="mt-2 text-sm text-gray-600 line-clamp-2">
                          "{witness.statement}"
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedWitness({ ...witness });
                      handleOpenDetail();
                    }}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                  {canUpdate && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setAction("Edit");
                          setSelectedWitness(witness);
                          handleOpenForm();
                        }}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedWitness(witness);
                          setIsOpenDelete(true);
                        }}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpenForm && (
        <WitnessForm
          action={action}
          complaintId={compliant?.id || ""}
          witness={action === "Edit" ? selectedWitness : undefined}
          isOpen={isOpenForm}
          onClose={handleCloseForm}
        />
      )}

      <WitnessDetail
        witness={selectedWitness}
        isOpen={isOpenDetail}
        onClose={handleCloseDetail}
      />

      {selectedWitness && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteWitness(selectedWitness)
              .then(() => {
                showTooltip("Witness successfully deleted!", "success");
                setRefetch();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail || errorHandler(error),
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title="Delete Witness"
          message={`Are you sure you want to delete ${selectedWitness?.full_name}? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default WitnessTab;
